// Notion API Service for StoryShot
// Saves scripts and video information to Notion database

interface NotionPageData {
  topic: string;
  script: string;
  videoUrl?: string;
  language: string;
  structure: string;
  length: string;
  timestamp: string;
}

export class NotionService {
  private static NOTION_API_URL = 'https://api.notion.com/v1';
  private static NOTION_VERSION = '2022-06-28';

  /**
   * Save story to Notion database via server API
   */
  static async saveToNotion(data: {
    topic: string;
    script: string;
    videoUrl?: string;
    language: string;
    structure: string;
    length: string;
  }): Promise<boolean> {
    try {
      const pageData: NotionPageData = {
        ...data,
        timestamp: new Date().toISOString()
      };

      // Create a comprehensive title with metadata
      const fullTitle = `[${data.length}] ${data.topic} - ${data.language}`;
      
      // Create formatted content with all metadata
      const formattedContent = `
📝 StoryShot AI Script
========================

📌 Topic: ${data.topic}
🌐 Language: ${data.language}
⏱️ Length: ${data.length}
🎬 Structure: ${data.structure}
📅 Created: ${new Date().toLocaleString('ko-KR')}
${data.videoUrl ? `🎥 Video: ${data.videoUrl}` : '📄 Status: Script Only'}

------------------------
SCRIPT
------------------------

${data.script}

------------------------
${this.generateTags(data.topic).map(tag => `#${tag.name}`).join(' ')}
      `.trim();

      // Use server-side API to save to Notion
      // Only send title and content - no custom properties needed
      const response = await fetch('/api/notion/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: fullTitle,
          content: formattedContent,
          properties: {} // Empty properties - we'll use just the Name field
        })
      });

      const result = await response.json();

      if (response.ok && result.ok) {
        console.log('Successfully saved to Notion via server:', result.id);
        
        // Store reference in localStorage
        if (result.id) {
          const recentPages = this.getRecentNotionPages();
          recentPages.unshift({
            id: result.id,
            topic: data.topic,
            timestamp: new Date().toISOString()
          });
          // Keep only last 10 references
          if (recentPages.length > 10) {
            recentPages.splice(10);
          }
          localStorage.setItem('notion_recent_pages', JSON.stringify(recentPages));
        }
        
        return true;
      } else {
        console.error('Failed to save to Notion:', result.error || 'Unknown error');
        return false;
      }
    } catch (error) {
      console.error('Error saving to Notion:', error);
      return false;
    }
  }

  /**
   * Create Notion page object with proper formatting
   */
  private static createNotionPage(databaseId: string, data: NotionPageData) {
    return {
      parent: {
        type: 'database_id',
        database_id: databaseId
      },
      properties: {
        // Title (제목)
        'Title': {
          title: [
            {
              text: {
                content: data.topic
              }
            }
          ]
        },
        // Date (날짜)
        'Date': {
          date: {
            start: data.timestamp
          }
        },
        // Language (언어)
        'Language': {
          select: {
            name: data.language
          }
        },
        // Length (길이)
        'Length': {
          select: {
            name: data.length
          }
        },
        // Structure (구조)
        'Structure': {
          select: {
            name: data.structure
          }
        },
        // Video URL (영상 링크)
        'Video URL': {
          url: data.videoUrl || null
        },
        // Status (상태)
        'Status': {
          select: {
            name: data.videoUrl ? '영상 완료' : '스크립트만'
          }
        },
        // Tags (태그)
        'Tags': {
          multi_select: this.generateTags(data.topic)
        }
      },
      children: [
        {
          object: 'block',
          type: 'heading_1',
          heading_1: {
            rich_text: [
              {
                type: 'text',
                text: {
                  content: '📝 스크립트'
                }
              }
            ]
          }
        },
        ...this.formatScriptBlocks(data.script),
        ...(data.videoUrl ? [
          {
            object: 'block',
            type: 'heading_2',
            heading_2: {
              rich_text: [
                {
                  type: 'text',
                  text: {
                    content: '🎥 생성된 영상'
                  }
                }
              ]
            }
          },
          {
            object: 'block',
            type: 'bookmark',
            bookmark: {
              url: data.videoUrl
            }
          }
        ] : []),
        {
          object: 'block',
          type: 'divider',
          divider: {}
        },
        {
          object: 'block',
          type: 'callout',
          callout: {
            rich_text: [
              {
                type: 'text',
                text: {
                  content: `Generated by StoryShot AI on ${new Date(data.timestamp).toLocaleString('ko-KR')}`
                }
              }
            ],
            icon: {
              emoji: '🤖'
            }
          }
        }
      ]
    };
  }

  /**
   * Format script into Notion blocks
   */
  private static formatScriptBlocks(script: string): any[] {
    const blocks: any[] = [];
    
    // Split by segments if they exist
    const segments = script.split(/SEGMENT (\d+)/);
    
    if (segments.length > 1) {
      for (let i = 1; i < segments.length; i += 2) {
        const segmentNumber = segments[i];
        const segmentContent = segments[i + 1]?.trim();
        
        if (segmentContent) {
          // Add segment header
          blocks.push({
            object: 'block',
            type: 'heading_3',
            heading_3: {
              rich_text: [
                {
                  type: 'text',
                  text: {
                    content: `세그먼트 ${segmentNumber}`
                  }
                }
              ]
            }
          });
          
          // Add segment content
          const lines = segmentContent.split('\n').filter(line => line.trim());
          for (const line of lines) {
            if (line.startsWith('Visual:')) {
              blocks.push({
                object: 'block',
                type: 'bulleted_list_item',
                bulleted_list_item: {
                  rich_text: [
                    {
                      type: 'text',
                      text: {
                        content: '👁️ '
                      }
                    },
                    {
                      type: 'text',
                      text: {
                        content: line,
                        annotations: {
                          bold: true
                        }
                      }
                    }
                  ]
                }
              });
            } else if (line.startsWith('Audio')) {
              blocks.push({
                object: 'block',
                type: 'bulleted_list_item',
                bulleted_list_item: {
                  rich_text: [
                    {
                      type: 'text',
                      text: {
                        content: '🎵 '
                      }
                    },
                    {
                      type: 'text',
                      text: {
                        content: line,
                        annotations: {
                          italic: true
                        }
                      }
                    }
                  ]
                }
              });
            } else if (line.trim()) {
              blocks.push({
                object: 'block',
                type: 'paragraph',
                paragraph: {
                  rich_text: [
                    {
                      type: 'text',
                      text: {
                        content: line
                      }
                    }
                  ]
                }
              });
            }
          }
        }
      }
    } else {
      // No segments, just add as paragraphs
      const lines = script.split('\n').filter(line => line.trim());
      for (const line of lines) {
        blocks.push({
          object: 'block',
          type: 'paragraph',
          paragraph: {
            rich_text: [
              {
                type: 'text',
                text: {
                  content: line
                }
              }
            ]
          }
        });
      }
    }
    
    return blocks;
  }

  /**
   * Generate tags from topic
   */
  private static generateTags(topic: string): Array<{name: string}> {
    const tags: Array<{name: string}> = [];
    
    // Add some automatic tags based on keywords
    const keywords = topic.toLowerCase().split(' ');
    
    if (keywords.some(k => k.includes('tech') || k.includes('기술'))) {
      tags.push({ name: 'Technology' });
    }
    if (keywords.some(k => k.includes('health') || k.includes('건강'))) {
      tags.push({ name: 'Health' });
    }
    if (keywords.some(k => k.includes('food') || k.includes('음식') || k.includes('요리'))) {
      tags.push({ name: 'Food' });
    }
    if (keywords.some(k => k.includes('travel') || k.includes('여행'))) {
      tags.push({ name: 'Travel' });
    }
    if (keywords.some(k => k.includes('education') || k.includes('교육') || k.includes('학습'))) {
      tags.push({ name: 'Education' });
    }
    
    // Add a general tag
    tags.push({ name: 'AI Generated' });
    
    return tags;
  }

  /**
   * Get recent Notion pages from localStorage
   */
  static getRecentNotionPages(): any[] {
    try {
      const stored = localStorage.getItem('notion_recent_pages');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error reading recent Notion pages:', error);
    }
    return [];
  }

  /**
   * Test Notion connection via server API
   */
  static async testConnection(): Promise<boolean> {
    try {
      // Test by attempting to save a test page
      const response = await fetch('/api/notion/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: '[Test] Connection Test',
          content: 'This is a test page to verify Notion connection.',
          properties: {} // No custom properties needed
        })
      });

      const result = await response.json();

      if (response.ok && result.ok) {
        console.log('Notion connection test successful:', result.id);
        return true;
      } else {
        console.error('Notion connection test failed:', result.error || 'Unknown error');
        return false;
      }
    } catch (error) {
      console.error('Error testing Notion connection:', error);
      return false;
    }
  }
}